/**
 * Finance V2 — Sensitivity tables + Capital Structure Comparison.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../../lib/errors";
import { Section, Kpi, CategoryChip, fmtMoney0, fmtPct } from "./_shared";

const SENS_PRESETS = {
  loss_rate: { label: "Loss rate", values: [0.02, 0.05, 0.08, 0.12, 0.18] },
  annual_cost_of_capital_pct: { label: "Cost of capital %", values: [6, 8, 10, 12, 15, 18] },
  facility_amount: { label: "Facility size", values: [1_000_000, 3_000_000, 5_000_000, 10_000_000] },
  avg_advance: { label: "Avg advance", values: [1500, 2000, 2500, 3000, 4000] },
  cases_per_month_start: { label: "Cases / mo (start)", values: [10, 20, 30, 50, 80] },
  reduction_rate: { label: "Reduction rate", values: [0.01, 0.03, 0.05, 0.08] },
  avg_duration_months: { label: "Avg duration (months)", values: [8, 12, 16, 20] },
  monthly_growth_pct: { label: "Monthly growth %", values: [0, 2, 5, 8] },
};

export default function FinanceSensitivity() {
  const [scenario, setScenario] = useState("base");
  const [variable, setVariable] = useState("loss_rate");
  const [structures, setStructures] = useState([]);
  const [structId, setStructId] = useState("");
  const [table, setTable] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareIds, setCompareIds] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/finance/v2/capital-structures");
      setStructures(data.structures || []);
      const current = (data.structures || []).find(s => s.is_current);
      if (current) setStructId(current.id);
      setCompareIds((data.structures || []).map(s => s.id));
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    try {
      const { data } = await api.post("/finance/v2/sensitivity", {
        scenario, capital_structure_id: structId || null,
        variable, values: SENS_PRESETS[variable].values,
      });
      setTable(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [scenario, variable, structId]);

  const runCompare = async () => {
    try {
      const { data } = await api.post("/finance/v2/capital-comparison", {
        scenario, capital_structure_ids: compareIds,
      });
      setCompare(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (compareIds.length) runCompare(); /* eslint-disable-next-line */ }, [scenario, compareIds]);

  return (
    <div className="p-6 max-w-[1400px]" data-testid="finance-sensitivity-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0A2540]">Sensitivity & Capital Comparison</h1>
          <div className="text-xs text-[#6B7280] mt-1">Stress-test each input independently. Compare capital structures side-by-side.</div>
        </div>
        <CategoryChip category="forecast" />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Scenario</label>
          <select value={scenario} onChange={(e) => setScenario(e.target.value)} className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="scenario-select">
            <option value="base">Base</option><option value="upside">Upside</option><option value="downside">Downside</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Variable</label>
          <select value={variable} onChange={(e) => setVariable(e.target.value)} className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" data-testid="variable-select">
            {Object.entries(SENS_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">Structure</label>
          <select value={structId} onChange={(e) => setStructId(e.target.value)} className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white">
            <option value="">— Select a Capital Structure —</option>
            {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <Section title={`Sensitivity — ${SENS_PRESETS[variable].label}`} category="forecast">
        {table ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="py-2">{SENS_PRESETS[variable].label}</th>
                <th className="text-right">Net Contribution</th>
                <th className="text-right">Margin</th>
                <th className="text-right">Return on Deployed</th>
                <th className="text-right">Peak Outstanding</th>
                <th className="text-right">Recycling</th>
                <th className="text-right">Gross Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(table.rows || []).map((r, i) => (
                <tr key={i} className="border-b border-[#F3F4F6]" data-testid={`sens-row-${i}`}>
                  <td className="py-2">{typeof r.value === "number" && r.value > 100 ? fmtMoney0(r.value) : r.value}</td>
                  <td className="tabular-nums text-right font-medium">{fmtMoney0(r.net_portfolio_contribution)}</td>
                  <td className="tabular-nums text-right">{fmtPct(r.contribution_margin_pct)}</td>
                  <td className="tabular-nums text-right">{fmtPct(r.return_on_deployed_capital)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.peak_outstanding)}</td>
                  <td className="tabular-nums text-right">{r.capital_recycling_ratio?.toFixed(2) || "—"}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.total_gross_realized_funding_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="text-sm text-[#6B7280]">Loading…</div>}
      </Section>

      <Section title="Capital Structure Comparison" category="forecast"
                subtitle="Same scenario applied to each configured structure. Add more structures under Capital Structures.">
        {compare.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No structures configured. Add at least one to compare.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="py-2">Structure</th><th>Provenance</th><th className="text-right">Facility</th>
                <th className="text-right">Cost %</th><th className="text-right">Net Contribution</th>
                <th className="text-right">Margin</th><th className="text-right">Return on Deployed</th>
                <th className="text-right">Peak Outstanding</th><th className="text-right">Max cases/mo</th>
              </tr>
            </thead>
            <tbody>
              {compare.map((r, i) => (
                <tr key={i} className="border-b border-[#F3F4F6]" data-testid={`compare-row-${i}`}>
                  <td className="py-2 font-medium">{r.name}</td>
                  <td><CategoryChip category={r.provenance === "actual" ? "contractual" : "forecast"} /></td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.facility_amount)}</td>
                  <td className="tabular-nums text-right">{r.annual_cost_of_capital_pct}%</td>
                  <td className="tabular-nums text-right font-semibold">{fmtMoney0(r.net_portfolio_contribution)}</td>
                  <td className="tabular-nums text-right">{fmtPct(r.contribution_margin_pct)}</td>
                  <td className="tabular-nums text-right">{fmtPct(r.return_on_deployed_capital)}</td>
                  <td className="tabular-nums text-right">{fmtMoney0(r.peak_outstanding)}</td>
                  <td className="tabular-nums text-right">{r.max_sustainable_cases_per_month || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
